import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { adminCourseCreateAction, adminCourseGetAction, adminCourseUpdateAction } from "../Action/action";
import config from "../coreFIles/config";
import CKEditorWithSource from "./CKEditorWithSource";
import axios from "axios";
import { getAdminAuthToken } from "../coreFIles/auth";

const ageGroups = ["5-10", "Ages 4-7", "Ages 8-12", "Ages 13+", "All Ages"];

const unlockOptions = [
  { value: "automatic", label: "Automatic (1 week interval)" },
  { value: "manual", label: "Manual (admin unlocks)" },
  { value: "on_completion", label: "On completion of previous week" }
];

function slugify(v) {
  return String(v || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

function editorPlainText(html) {
  return String(html || "")
    .replace(/&nbsp;/g, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildApiUrl(endpoint) {
  const base = (config.apiUrl || "").replace(/\/+$/, "");
  const path = String(endpoint || "").replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
}

class CkUploadAdapter {
  constructor(loader) {
    this.loader = loader;
    this.controller = new AbortController();
  }

  abort() {
    try {
      this.controller.abort();
    } catch (e) {}
  }

  async upload() {
    const file = await this.loader.file;
    const fd = new FormData();
    fd.append("upload", file);

    const token = getAdminAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const resp = await axios.post(buildApiUrl("/admin/upload/image"), fd, {
      headers,
      withCredentials: true,
      signal: this.controller.signal
    });

    const url = resp?.data?.url;
    if (!url) throw new Error("Upload failed");
    return { default: resolvePublicUrl(url) };
  }
}

function CkUploadAdapterPlugin(editor) {
  // eslint-disable-next-line no-param-reassign
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => new CkUploadAdapter(loader);
}

export default function CourseForm({ mode }) {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [loadingCourse, setLoadingCourse] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [weeksCount, setWeeksCount] = useState(8);
  const [price, setPrice] = useState(499);
  const [currency] = useState("AUD");
  const [status, setStatus] = useState("Draft"); // Draft/Active (others mapped to Draft)

  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState("");
  const thumbnailInputRef = useRef(null);

  const [videoUrl, setVideoUrl] = useState("");
  const [existingIntroVideoUrl, setExistingIntroVideoUrl] = useState("");
  const [existingVideoType, setExistingVideoType] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const videoInputRef = useRef(null);

  const [unlockMethod, setUnlockMethod] = useState("automatic");

  const isEdit = mode === "edit";

  useEffect(() => {
    if (!thumbnailFile) {
      if (thumbnailPreviewUrl) setThumbnailPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(thumbnailFile);
    setThumbnailPreviewUrl(url);
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thumbnailFile]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!isEdit || !courseId) return;
      setLoadingCourse(true);
      setError("");
      try {
        const res = await adminCourseGetAction(courseId);
        if (!res?.success) throw new Error(res?.message || res?.msg || "Failed to load course.");
        const c = res.data || {};

        if (cancelled) return;
        setTitle(c?.title || "");
        setSlug(c?.slug || "");
        setShortDescription(c?.short_description || "");
        setDescription(c?.description || "");
        setAgeGroup(c?.age_group || "");
        setWeeksCount(c?.weeks_count !== undefined && c?.weeks_count !== null ? Number(c.weeks_count) : 0);
        setPrice(c?.price !== undefined && c?.price !== null ? Number(c.price) : 0);
        setStatus(Number(c?.active) ? "Active" : "Draft");
        setThumbnailUrl(c?.thumbnail_url || "");
        setThumbnailFile(null);
        setThumbnailPreviewUrl("");
        setUnlockMethod(c?.unlock_method || "automatic");

        const vType = c?.video_type || "";
        const vUrl = c?.intro_video_url || "";
        setExistingVideoType(vType);
        setExistingIntroVideoUrl(vUrl);
        // Only show URL in the field for thirdparty videos; keep selfhosted as-is without forcing overwrite.
        if (vType === "thirdparty") setVideoUrl(vUrl);
        else setVideoUrl("");
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || "Failed to load course.");
      } finally {
        if (!cancelled) setLoadingCourse(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [courseId, isEdit]);

  const validate = () => {
    const errs = {};
    if (!String(title || "").trim()) errs.title = "Course Name is required.";
    if (!editorPlainText(shortDescription)) errs.shortDescription = "Short Description is required.";
    if (!editorPlainText(description)) errs.description = "Full Description is required.";
    if (!String(ageGroup || "").trim()) errs.ageGroup = "Age Group is required.";
    if (!unlockMethod) errs.unlockMethod = "Unlock Method is required.";

    const w = Number(weeksCount);
    if (weeksCount === "" || weeksCount === null || weeksCount === undefined || Number.isNaN(w) || w < 0) {
      errs.weeksCount = "Please enter a valid number of weeks.";
    }

    const p = Number(price);
    if (price === "" || price === null || price === undefined || Number.isNaN(p) || p < 0) {
      errs.price = "Please enter a valid price.";
    }

    return errs;
  };

  const onSave = async () => {
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    setError("");
    try {
      const formData = new FormData();
      const computedSlug = (slug && slug.trim()) ? slug.trim() : slugify(title);
      formData.append("title", title.trim());
      formData.append("slug", computedSlug);
      formData.append("price", String(Number(price) || 0));
      formData.append("currency", currency || "AUD");
      formData.append("age_group", ageGroup);
      formData.append("weeks_count", String(Number(weeksCount) || 0));
      formData.append("short_description", shortDescription);
      formData.append("description", description);
      formData.append("unlock_method", unlockMethod);

      const isActive = status === "Active" ? 1 : 0;
      formData.append("active", String(isActive));

      if (thumbnailFile) formData.append("thumbnail", thumbnailFile);
      else if (thumbnailUrl) formData.append("thumbnail_url", thumbnailUrl);

      if (videoFile) {
        formData.append("video", videoFile);
      } else if (String(videoUrl || "").trim()) {
        formData.append("intro_video_url", String(videoUrl).trim());
        formData.append("video_type", "thirdparty");
      } else if (!isEdit) {
        // create mode: intro video optional, omit if empty
      } else {
        // edit mode: omit fields if empty to avoid overwriting existing video (selfhosted/thirdparty)
      }

      const res = isEdit
        ? await adminCourseUpdateAction(courseId, formData)
        : await adminCourseCreateAction(formData);

      if (!res?.success) throw new Error(res?.message || res?.msg || "Save failed");

      toast.success(`Course ${isEdit ? "updated" : "created"}`, { toastId: "course-save" });
      navigate("/courses");
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Save failed";
      setError(msg);
      toast.error(msg, { toastId: "course-save" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      {isEdit && loadingCourse ? (
        <div className="loading-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="loading-spinner" aria-hidden="true" />
        </div>
      ) : null}

      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Could not load/save course.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to="/courses">Courses</Link>
          <span>›</span>
          <span>{isEdit ? "Edit Course" : "Add New Course"}</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/courses" className="btn btn-outline btn-sm">
            ← Back
          </Link>
        </div>
      </div>

      <div className="grid-2">
        <div>
          <div className="card mb-6">
            <h2 className="section-title">Course Details</h2>

            <div className="form-group">
              <label>
                Course Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter course name..."
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setFieldErrors((p) => ({ ...p, title: "" }));
                  if (!isEdit && !slug) setSlug(slugify(e.target.value));
                }}
                aria-invalid={!!fieldErrors.title}
                style={fieldErrors.title ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.title ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {fieldErrors.title}
                </div>
              ) : null}
            </div>

            <div className="form-group">
              <label>
                Short Description <span className="text-danger">*</span>
              </label>
              <CKEditorWithSource
                heightClassName="ckeditor-height-200"
                containerStyle={
                  fieldErrors.shortDescription
                    ? { border: "1px solid var(--danger)", borderRadius: 6, overflow: "hidden" }
                    : { border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }
                }
                data={shortDescription}
                config={{
                  extraPlugins: [CkUploadAdapterPlugin],
                  toolbar: ["heading", "|", "bold", "italic", "link", "bulletedList", "numberedList", "|", "undo", "redo", "|", "imageUpload"]
                }}
                onChange={(html) => {
                  setShortDescription(html);
                  setFieldErrors((p) => ({ ...p, shortDescription: "" }));
                }}
              />
              {fieldErrors.shortDescription ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {fieldErrors.shortDescription}
                </div>
              ) : null}
            </div>

            <div className="form-group">
              <label>
                Full Description <span className="text-danger">*</span>
              </label>
              <CKEditorWithSource
                heightClassName="ckeditor-height-400"
                containerStyle={
                  fieldErrors.description
                    ? { border: "1px solid var(--danger)", borderRadius: 6, overflow: "hidden" }
                    : { border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }
                }
                data={description}
                config={{
                  extraPlugins: [CkUploadAdapterPlugin],
                  toolbar: ["heading", "|", "bold", "italic", "link", "bulletedList", "numberedList", "|", "undo", "redo", "|", "imageUpload"]
                }}
                onChange={(html) => {
                  setDescription(html);
                  setFieldErrors((p) => ({ ...p, description: "" }));
                }}
              />
              {fieldErrors.description ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {fieldErrors.description}
                </div>
              ) : null}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Age Group <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  value={ageGroup}
                  onChange={(e) => {
                    setAgeGroup(e.target.value);
                    setFieldErrors((p) => ({ ...p, ageGroup: "" }));
                  }}
                  aria-invalid={!!fieldErrors.ageGroup}
                  style={fieldErrors.ageGroup ? { borderColor: "var(--danger)" } : undefined}
                >
                  <option value="">Select age group...</option>
                  {ageGroups.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
                {fieldErrors.ageGroup ? (
                  <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                    {fieldErrors.ageGroup}
                  </div>
                ) : null}
              </div>
              <div className="form-group">
                <label>Number of Weeks</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="e.g. 8"
                  min="0"
                  max="52"
                  value={weeksCount}
                  onChange={(e) => {
                    setWeeksCount(e.target.value === "" ? "" : Number(e.target.value));
                    setFieldErrors((p) => ({ ...p, weeksCount: "" }));
                  }}
                  aria-invalid={!!fieldErrors.weeksCount}
                  style={fieldErrors.weeksCount ? { borderColor: "var(--danger)" } : undefined}
                />
                {fieldErrors.weeksCount ? (
                  <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                    {fieldErrors.weeksCount}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Price (AUD)</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="e.g. 499"
                  value={price}
                  onChange={(e) => {
                    setPrice(e.target.value === "" ? "" : Number(e.target.value));
                    setFieldErrors((p) => ({ ...p, price: "" }));
                  }}
                  aria-invalid={!!fieldErrors.price}
                  style={fieldErrors.price ? { borderColor: "var(--danger)" } : undefined}
                />
                {fieldErrors.price ? (
                  <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                    {fieldErrors.price}
                  </div>
                ) : null}
              </div>
              <div className="form-group">
                <label>Status</label>
                <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option>Draft</option>
                  <option>Active</option>
                  <option>Coming Soon</option>
                  <option>Archived</option>
                </select>
              </div>
            </div>
          </div>

          {/* Onboarding Questions section (wireframe) intentionally left static for now */}
        </div>

        <div>
          <div className="card mb-6">
            <h2 className="section-title">Course Thumbnail</h2>
            <div
              className="upload-area p-sm"
              role="button"
              tabIndex={0}
              onClick={() => thumbnailInputRef.current?.click?.()}
              onKeyDown={(e) => e.key === "Enter" && thumbnailInputRef.current?.click?.()}
            >
              <div className="avatar-upload" style={{ overflow: "hidden" }}>
                {thumbnailPreviewUrl || thumbnailUrl ? (
                  <img
                    src={thumbnailPreviewUrl || resolvePublicUrl(thumbnailUrl)}
                    alt="Thumbnail preview"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <span style={{ color: "var(--primary)", display: "inline-flex" }} aria-hidden="true">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="3" />
                      <circle cx="9" cy="9" r="2.5" />
                      <path d="M3 16l5-5 4 4 3-3 6 6" />
                    </svg>
                  </span>
                )}
              </div>
              <p className="font-sm">
                <strong>Click to upload</strong> or drag and drop
              </p>
              <p className="small-label">PNG, JPG up to 2MB</p>
            </div>
            <input
              ref={thumbnailInputRef}
              type="file"
              accept="image/png,image/jpeg"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                setThumbnailFile(f);
                if (f) setThumbnailUrl("");
              }}
            />
          </div>

          <div className="card mb-6">
            <h2 className="section-title">Intro Video</h2>
            <div className="form-group">
              <label>Video URL</label>
              <input
                type="text"
                className="form-control"
                placeholder="https://..."
                value={videoUrl}
                onChange={(e) => {
                  setVideoUrl(e.target.value);
                }}
              />
            </div>
            {isEdit && existingIntroVideoUrl ? (
              <p className="small-text" style={{ marginTop: -6 }}>
                <strong>Current video:</strong>{" "}
                {existingVideoType === "selfhosted" ? (
                  <a href={resolvePublicUrl(existingIntroVideoUrl)} target="_blank" rel="noreferrer">
                    Uploaded video
                  </a>
                ) : (
                  <a href={existingIntroVideoUrl} target="_blank" rel="noreferrer">
                    {existingIntroVideoUrl}
                  </a>
                )}
              </p>
            ) : null}
            <div className="form-group">
              <label>Or Upload Video</label>
              <div className="flex items-center gap-2">
                <button className="btn btn-outline btn-sm" type="button" onClick={() => videoInputRef.current?.click()}>
                  Upload Video
                </button>
                {videoFile ? <span className="small-text">{videoFile.name}</span> : null}
              </div>
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  setVideoFile(f);
                  if (f) setVideoUrl("");
                }}
              />
            </div>
            <p className="small-text">This video is shown on the course landing page in the app before purchase.</p>
          </div>

          <div className="card mb-6">
            <h2 className="section-title">Week Unlock Settings</h2>
            <div className="form-group">
              <label>
                Unlock Method <span className="text-danger">*</span>
              </label>
              <select
                className="form-control"
                value={unlockMethod}
                onChange={(e) => {
                  setUnlockMethod(e.target.value);
                  setFieldErrors((p) => ({ ...p, unlockMethod: "" }));
                }}
                aria-invalid={!!fieldErrors.unlockMethod}
                style={fieldErrors.unlockMethod ? { borderColor: "var(--danger)" } : undefined}
              >
                {unlockOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {fieldErrors.unlockMethod ? (
              <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                {fieldErrors.unlockMethod}
              </div>
            ) : null}
            <p className="small-text">
              <strong>Automatic:</strong> Each week unlocks exactly 7 days after the previous week was unlocked. Week 1 is available immediately after purchase.
            </p>
          </div>

          <div className="card">
            <h2 className="section-title">Summary</h2>
            <div className="summary-meta">
              <div className="flex justify-between">
                <span>Status:</span> <span className="badge badge-gray">{status}</span>
              </div>
              <div className="flex justify-between">
                <span>Weeks:</span> <span>{weeksCount || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span>Price:</span> <span>{price ? `AUD ${price}` : "—"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button className="btn btn-primary" type="button" onClick={() => onSave()} disabled={saving || loadingCourse}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
            <path d="M17 21v-8H7v8" />
            <path d="M7 3v5h8" />
          </svg>
          {saving ? "Saving..." : "Save Course"}
        </button>
        <Link to="/courses" className="btn btn-outline">
          Cancel
        </Link>
      </div>
    </div>
  );
}

